import { useState } from "react";
import { deriveAuthSecret, derivePasswordWrapKey, unwrapChatKey } from "../lib/crypto.js";
import { getAccountSalt, login as loginRequest } from "../lib/api.js";
import { saveSession, type Session } from "../lib/session.js";

interface LoginProps {
  onSwitchToSetup: () => void;
  onSwitchToRecovery: () => void;
  onLoggedIn: (session: Session) => void;
}

const FRIENDLY_ERRORS: Record<string, string> = {
  account_not_found: "Invalid username or password.",
  invalid_username_or_password: "Invalid username or password.",
  too_many_failed_login_attempts_try_again_later: "Too many failed attempts — try again later."
};

/**
 * Username + password login (Decision 4), usable from any device at any
 * time after Setup: fetch the account's non-secret salt, derive
 * `authSecret`/`passwordWrapKey` locally, authenticate, then unwrap the
 * server-returned `wrapped_chat_key_password` blob to recover `chatKey`.
 *
 * Revision 5 (Decision 4): `chatKey` is no longer directly derivable from
 * the password — this now requires the extra unwrap step below.
 */
export default function Login({ onSwitchToSetup, onSwitchToRecovery, onLoggedIn }: LoginProps) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [status, setStatus] = useState<"idle" | "working" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    setErrorMessage(null);
    setStatus("working");
    try {
      const trimmedUsername = username.trim();
      const { salt } = await getAccountSalt(trimmedUsername);
      const [authSecret, passwordWrapKey] = await Promise.all([
        deriveAuthSecret(password, salt),
        derivePasswordWrapKey(password, salt)
      ]);
      const result = await loginRequest({
        username: trimmedUsername,
        authSecret,
        deviceType: "web",
        deviceName: "PC browser"
      });
      const chatKey = unwrapChatKey(result.wrappedChatKeyPassword, passwordWrapKey);
      if (!chatKey) {
        setErrorMessage("Failed to unlock your data on this device. Try again, or use account recovery.");
        setStatus("error");
        return;
      }
      const session: Session = {
        username: result.username,
        deviceId: result.deviceId,
        token: result.deviceToken,
        chatKey
      };
      saveSession(session);
      onLoggedIn(session);
    } catch (err) {
      const message = err instanceof Error ? err.message : "login_failed";
      setErrorMessage(FRIENDLY_ERRORS[message] ?? message);
      setStatus("error");
    }
  }

  return (
    <div className="rounded-xl border border-slate-800 bg-gray-900 p-4">
      <h1 className="text-xl font-semibold text-slate-100">Log in to Bridge</h1>
      <form onSubmit={handleSubmit} className="mt-3 flex flex-col gap-3">
        <label className="flex flex-col gap-1 text-sm text-slate-300">
          Username
          <input
            type="text"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            autoComplete="username"
            className="w-full rounded-lg border border-slate-700 bg-[#0b1220] px-3 py-2 text-slate-200"
          />
        </label>
        <label className="flex flex-col gap-1 text-sm text-slate-300">
          Password
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            className="w-full rounded-lg border border-slate-700 bg-[#0b1220] px-3 py-2 text-slate-200"
          />
        </label>
        <button
          type="submit"
          disabled={status === "working" || !username.trim() || !password}
          className="cursor-pointer rounded-lg bg-blue-600 px-4 py-2.5 text-white disabled:cursor-not-allowed disabled:opacity-60"
        >
          {status === "working" ? "Logging in…" : "Log in"}
        </button>
        {status === "error" && errorMessage && <p className="text-sm text-red-400">{errorMessage}</p>}
      </form>
      <p className="mt-3 text-sm text-slate-400">
        <button type="button" onClick={onSwitchToRecovery} className="cursor-pointer border-none bg-transparent p-0 text-blue-400">
          Forgot password?
        </button>
      </p>
      <p className="mt-1 text-sm text-slate-400">
        First time setting up Bridge?{" "}
        <button type="button" onClick={onSwitchToSetup} className="cursor-pointer border-none bg-transparent p-0 text-blue-400">
          Create the account
        </button>
      </p>
    </div>
  );
}
